import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSkinSession } from './SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import {
  getPageSetupDataKey,
  storePageSetupData,
  applyPageSetupResult,
} from '../components/PageBackgroundSync';
import { getPlanFlags } from '../config/skinAnalysisFeatureFlags';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { parseSegmentationPayload } from '../utils/segmentation';
import { fetchSkinAnalysisBundle } from '../utils/skinAnalysisBundle';
import { appendFranchiseThemeParams, franchiseAuthHeaders } from '../../shared/franchiseFlowAuth';

const VALID_PLANS = ['standard', 'growth', 'pro'];

function normalizePlan(value) {
  if (value == null || value === '') return null;
  const p = String(value).toLowerCase().trim();
  return VALID_PLANS.includes(p) ? p : null;
}

const SkinAnalysisPlanContext = createContext(null);

function normalizeCaptureMode(value) {
  if (value == null || value === '') return null;
  const v = String(value).toLowerCase().trim();
  return v === 'side' || v === 'basic' ? v : null;
}

export function SkinAnalysisPlanProvider({ children }) {
  const { token } = useSkinSession();
  const [plan, setPlan] = useState(null);
  const [captureMode, setCaptureMode] = useState(null);
  const [aiMode, setAiMode] = useState(null);
  const [summaryAiMode, setSummaryAiMode] = useState(null);
  const [aiGeneratedSummary, setAiGeneratedSummary] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!token || !token.trim()) {
      setPlan(null);
      setCaptureMode(null);
      setAiMode(null);
      setSummaryAiMode(null);
      setAiGeneratedSummary(null);
      setIsReady(true);
      return;
    }

    const dataKey = getPageSetupDataKey(token);
    let cancelled = false;

    const applyPlan = (result) => {
      if (cancelled || !result) return;
      const externalFields =
        result?.external_fields ||
        result?.data?.external_fields ||
        (Array.isArray(result) && result[0]?.external_fields);
      const rawPlan = externalFields?.plan;
      setPlan(normalizePlan(rawPlan));
    };

    const applyAiMode = (result) => {
      if (cancelled) return;
      const rawAiMode =
        result?.ai_mode ??
        result?.data?.ai_mode ??
        (Array.isArray(result) && result[0]?.ai_mode);
      const mode = rawAiMode && ['advanced', 'basic'].includes(String(rawAiMode).toLowerCase().trim())
        ? String(rawAiMode).toLowerCase().trim()
        : null;
      setSummaryAiMode(mode);
    };

    try {
      const stored = dataKey ? sessionStorage.getItem(dataKey) : null;
      if (stored) {
        const parsed = JSON.parse(stored);
        applyPageSetupResult(parsed);
        applyPlan(parsed);
      }
    } catch (_) {}

    const pageSetupQs = appendFranchiseThemeParams({ _: String(Date.now()) });
    const pageSetupUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup?${pageSetupQs.toString()}`;
    const summaryUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/summary`;

    fetchWithTimeout(pageSetupUrl, {
      timeoutMs: 20000,
      cache: 'no-store',
      credentials: 'include',
      headers: franchiseAuthHeaders(),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled || !result) return;
        try {
          storePageSetupData(token, result);
        } catch (_) {}
        applyPageSetupResult(result);
        applyPlan(result);
      })
      .catch(() => {
        if (!cancelled) {
          setPlan(null);
        }
      });

    fetchSkinAnalysisBundle(token)
      .then((result) => {
        if (cancelled || !result) return;
        const rawCaptureMode = result?.capture_mode;
        setCaptureMode(normalizeCaptureMode(rawCaptureMode));
        const parsed = parseSegmentationPayload(result);
        setAiMode(parsed.aiMode);
        setAiGeneratedSummary(parsed.aiGeneratedSummary);
        setIsReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setCaptureMode(null);
          setAiMode(null);
          setAiGeneratedSummary(null);
          setIsReady(true);
        }
      });

    fetchWithTimeout(summaryUrl, { timeoutMs: 20000 })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled) return;
        applyAiMode(result);
      })
      .catch(() => {
        if (!cancelled) {
          setSummaryAiMode(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const flags = getPlanFlags(plan);
  // In salon flow, capture_mode is authoritative for side capture.
  // "side" => front+left+right, "basic" (or missing/invalid) => front only.
  const enableSideCapture = captureMode === 'side';
  // Segmentation ai_mode is authoritative; /summary is fallback when segmentation omits it.
  const resolvedAiMode = aiMode ?? summaryAiMode;
  const enableOllama = aiGeneratedSummary !== 'no';
  const value = {
    plan,
    captureMode,
    aiMode: resolvedAiMode,
    aiGeneratedSummary,
    isReady,
    ...flags,
    ENABLE_SIDE_CAPTURE: enableSideCapture,
    ENABLE_OLLAMA_DESCRIPTIONS: enableOllama,
    ENABLE_OLLAMA_SUMMARY: enableOllama,
    ENABLE_OLLAMA_ROUTINE: false,
  };

  return (
    <SkinAnalysisPlanContext.Provider value={value}>
      {children}
    </SkinAnalysisPlanContext.Provider>
  );
}

export function useSkinAnalysisPlan() {
  const ctx = useContext(SkinAnalysisPlanContext);
  if (!ctx) {
    throw new Error('useSkinAnalysisPlan must be used within SkinAnalysisPlanProvider');
  }
  return ctx;
}
